"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { Check, ArrowRight, Sparkles, TrendingDown } from "@/components/icons";
import { cn, fmtPrice as formatPrice } from "@/lib/utils";
import { CONTACT_SALES_URL, ROUTES } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { brandActionClasses, buttonVariants } from "@/components/ui/button";
import { useProductsInfo } from "@/lib/products-info";
import { type GbTier } from "@/lib/pricing-utils";
import { buildCheckoutUrl } from "@/lib/checkout-url";
import { useAffiliateRef } from "@/hooks/use-affiliate-ref";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";

interface Props {
  /** Full set of per-GB tiers, already built from the products API (the "source array"). */
  tiers: GbTier[];
  /** Display names for the first tiers rendered as cards, by position. */
  planNames: readonly string[];
  /** Highlights shown on every card. */
  cardHighlights: readonly string[];
  /** Features listed once in the "Every plan includes" block. */
  sharedFeatures: readonly string[];
  /** Enterprise upsell copy. */
  enterpriseTitle: string;
  enterpriseDescription: string;
  /** Index (within the card tiers) flagged as "Most popular". */
  popularIdx?: number;
  /** Cap how many tiers render as cards (default 4). Pass 3 for a representative slice. */
  maxCards?: number;
  /** When set, replaces the higher-volume list and enterprise upsell with a single link out to
   *  the full pricing page — used where this grid is a summary rather than the definitive price
   *  list (e.g. embedded in a product page). */
  moreLink?: { label: string; href: string };
}

function fmtGb(gb: number) {
  return gb >= 1000 ? `${gb / 1000} TB` : `${gb} GB`;
}

export default function PricingTierGrid({
  tiers,
  planNames,
  cardHighlights,
  sharedFeatures,
  enterpriseTitle,
  enterpriseDescription,
  popularIdx = 2,
  maxCards = 4,
  moreLink,
}: Props) {
  const prefersReducedMotion = useReducedMotion();
  const info = useProductsInfo();
  const maintenanceMode = useMaintenanceMode();

  const { ref: affiliateRef, couponCode, discountRate } = useAffiliateRef();
  const discountPct = discountRate ? parseFloat(discountRate) : 0;
  const hasDiscount = discountPct > 0 && discountPct < 100;
  const applyDiscount = (n: number) =>
    hasDiscount ? Math.round(n * (1 - discountPct / 100) * 100) / 100 : n;

  // Highest per-GB rate is the baseline we measure volume savings against.
  const basePPG = Math.max(...tiers.map((t) => t.monthlyPPG));
  const savingsPct = (ppg: number) => (basePPG > 0 ? Math.round((1 - ppg / basePPG) * 100) : 0);

  const checkoutBase = info?.checkoutBaseUrl;

  function getCheckoutHref(tier: GbTier): string {
    if (maintenanceMode) return MAINTENANCE_PATH;
    if (checkoutBase && tier.productId && tier.variationId) {
      return buildCheckoutUrl(
        checkoutBase,
        tier.productId,
        tier.variationId,
        affiliateRef,
        couponCode
      );
    }
    return ROUTES.signup;
  }

  const cardTiers = tiers.slice(0, maxCards);
  const listTiers = moreLink ? [] : tiers.slice(maxCards);
  const hasVolumeSavings = listTiers.some((t) => savingsPct(t.monthlyPPG) > 0);

  return (
    <div className="flex flex-col gap-10">
      {/* First 4 tiers as columns — a floating plan card on top, the shared
          highlights below it as plain dot-bulleted rows. The column itself is
          only a wrapper: the bordered card is the floating one inside, so the
          wrapper carries no surface of its own or the two would nest. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
        {cardTiers.map((tier, i) => {
          const isPopular = i === popularIdx;
          const ppg = applyDiscount(tier.monthlyPPG);
          const total = applyDiscount(tier.totalPrice);
          const save = savingsPct(tier.monthlyPPG);
          const name = planNames[i] ?? fmtGb(tier.gb);
          return (
            <div key={tier.gb} className="flex flex-col">
              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={scrollRevealViewport}
                transition={{ duration: 0.5, delay: prefersReducedMotion ? 0 : i * 0.08 }}
                className="flex flex-1 flex-col"
              >
                {/* Floating plan card */}
                <div
                  className={cn(
                    "flex flex-col gap-4 rounded-2xl p-6 transition-shadow duration-300 hover:shadow-md",
                    isPopular
                      ? "border border-transparent"
                      : "border-border bg-card border shadow-sm"
                  )}
                  style={
                    isPopular
                      ? {
                          background: `linear-gradient(135deg, color-mix(in srgb, var(--brand-green) 18%, var(--card)) 0%, color-mix(in srgb, var(--brand-yellow) 10%, var(--card)) 100%)`,
                          boxShadow: `0 0 0 1px color-mix(in srgb, var(--brand-green) 33%, transparent), 0 8px 30px -12px color-mix(in srgb, var(--brand-green) 25%, transparent)`,
                        }
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-brand-green text-[11px] font-semibold tracking-wider uppercase">
                      {name}
                    </h3>
                    {isPopular && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--brand-yellow) 15%, transparent)",
                          color: "var(--yellow-accent)",
                          border:
                            "1px solid color-mix(in srgb, var(--brand-yellow) 26%, transparent)",
                        }}
                      >
                        <Sparkles size={10} aria-hidden="true" />
                        Most popular
                      </span>
                    )}
                  </div>

                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-foreground text-4xl font-bold tracking-tight tabular-nums">
                        ${formatPrice(total)}
                      </span>
                      <span className="text-muted-foreground text-sm">/ month</span>
                      {hasDiscount && (
                        <span className="text-muted-foreground text-sm tabular-nums line-through">
                          ${formatPrice(tier.totalPrice)}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-1.5 text-sm">
                      {fmtGb(tier.gb)} at{" "}
                      <span className="text-foreground font-semibold tabular-nums">
                        ${formatPrice(ppg)}/GB
                      </span>
                      {hasDiscount && discountRate ? (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{
                            backgroundColor: `color-mix(in srgb, var(--brand-green) 13%, transparent)`,
                            color: "var(--brand-green)",
                            border: `1px solid color-mix(in srgb, var(--brand-green) 21%, transparent)`,
                          }}
                        >
                          {discountRate} off
                        </span>
                      ) : (
                        save > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-xs font-semibold"
                            style={{ color: "var(--brand-green)" }}
                          >
                            <TrendingDown size={11} aria-hidden="true" />
                            Save {save}%
                          </span>
                        )
                      )}
                    </p>
                  </div>

                  <Link
                    href={getCheckoutHref(tier)}
                    aria-label={`Buy now — ${name} plan`}
                    className={cn(
                      buttonVariants({ variant: "default", size: "lg" }),
                      "w-full justify-center rounded-xl font-semibold"
                    )}
                  >
                    Buy Now
                    <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
                  </Link>
                </div>

                {/* Highlights below the card — plain dot-bulleted rows */}
                <ul
                  className="mt-5 flex flex-col gap-2.5 px-2 pb-1"
                  aria-label={`${name} plan highlights`}
                >
                  {cardHighlights.map((h) => (
                    <li key={h} className="text-muted-foreground flex items-start gap-2.5 text-sm">
                      <span
                        className="bg-muted-foreground/40 mt-1.75 h-1 w-1 shrink-0 rounded-full"
                        aria-hidden="true"
                      />
                      {h}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          );
        })}
      </div>

      {/* Compact/product-page view: skip the ladder detail and point at the full pricing page. */}
      {moreLink && (
        <div className="flex justify-center">
          <Link
            href={moreLink.href}
            className={cn(buttonVariants({ variant: "outline", size: "lg" }), "font-medium")}
          >
            {moreLink.label}
            <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Remaining (higher-volume) tiers as a list */}
      {listTiers.length > 0 && (
        <div>
          <div className="mb-3 flex items-baseline justify-between">
            <h3 className="text-foreground text-base font-bold">Higher-volume plans</h3>
            <span className="text-muted-foreground text-xs">
              {hasVolumeSavings
                ? "Lower price per GB as you scale"
                : "Same flat rate at every tier"}
            </span>
          </div>
          <div className="border-border divide-border bg-card divide-y overflow-hidden rounded-xl border">
            {listTiers.map((tier) => {
              const ppg = applyDiscount(tier.monthlyPPG);
              const total = applyDiscount(tier.totalPrice);
              const save = savingsPct(tier.monthlyPPG);
              return (
                <div
                  key={tier.gb}
                  className="hover:bg-muted/30 flex flex-col gap-3 px-5 py-4 transition-colors sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-foreground w-16 text-base font-bold tabular-nums">
                      {fmtGb(tier.gb)}
                    </span>
                    {hasDiscount && discountRate ? (
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{
                          backgroundColor: `color-mix(in srgb, var(--brand-green) 13%, transparent)`,
                          color: "var(--brand-green)",
                          border: `1px solid color-mix(in srgb, var(--brand-green) 21%, transparent)`,
                        }}
                      >
                        {discountRate} off
                      </span>
                    ) : (
                      save > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{
                            backgroundColor:
                              "color-mix(in srgb, var(--brand-green) 9%, transparent)",
                            color: "var(--brand-green)",
                          }}
                        >
                          <TrendingDown size={11} aria-hidden="true" />
                          Save {save}%
                        </span>
                      )
                    )}
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
                    <div className="text-left sm:text-right">
                      <span className="text-foreground text-base font-bold tabular-nums">
                        ${formatPrice(total)}
                      </span>
                      {hasDiscount && (
                        <span className="text-muted-foreground ml-1.5 text-xs tabular-nums line-through">
                          ${formatPrice(tier.totalPrice)}
                        </span>
                      )}
                      <span className="text-muted-foreground ml-2 text-xs tabular-nums">
                        ${formatPrice(ppg)}/GB
                      </span>
                    </div>
                    <Link
                      href={getCheckoutHref(tier)}
                      aria-label={`Buy now — ${fmtGb(tier.gb)} plan`}
                      className={cn(
                        buttonVariants({ variant: "secondary", size: "sm" }),
                        // taller tap target on mobile (44px), compact on desktop
                        "h-11 w-full font-semibold sm:h-8 sm:w-auto sm:shrink-0",
                        // solid brand blue in both themes, lightening on hover
                        brandActionClasses,
                        "border-transparent hover:text-white"
                      )}
                    >
                      Buy Now
                      <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shared inclusions — every plan, listed once */}
      <div className="border-border bg-muted/20 rounded-xl border p-6">
        <p className="text-foreground mb-4 text-sm font-semibold">Every plan includes</p>
        <ul
          className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="Included with every plan"
        >
          {sharedFeatures.map((f) => (
            <li key={f} className="text-muted-foreground flex items-start gap-2 text-sm">
              <Check
                size={14}
                className="mt-0.5 shrink-0"
                style={{ color: "var(--brand-green)" }}
                aria-hidden="true"
              />
              {f}
            </li>
          ))}
        </ul>
      </div>

      {/* Enterprise upsell — part of the full ladder, so skipped in the compact/moreLink view. */}
      {!moreLink && (
        <div className="border-border bg-muted/30 flex flex-col justify-between gap-4 rounded-xl border p-5 sm:flex-row sm:items-center">
          <div>
            <p className="text-foreground text-sm font-semibold">{enterpriseTitle}</p>
            <p className="text-muted-foreground mt-0.5 text-sm">{enterpriseDescription}</p>
          </div>
          <Link
            href={CONTACT_SALES_URL}
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "shrink-0 font-medium"
            )}
          >
            Talk to Sales
            <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
          </Link>
        </div>
      )}
    </div>
  );
}
